<template>
  <div>
    <LfBlueTitleComponent :title="TEXT.title"/>
    <div class="step-wizard-container">
      <div class="step-section">
        <h3 class="step-section-title">{{ TEXT.step1Title }}</h3>
        <p class="step-description">{{ TEXT.step1Description }}</p>
        <div class="download-button-container">
          <LfLabelButtonComponent
            :prependIcon="Icons.DOWNLOAD"
            :action="downloadImportExpedientTemplate"
            :text="TEXT.downloadTemplate"
            variant="outlined"
            size="md"
            :disabled="isDownloadingTemplate"
            class="download-btn"
          />
        </div>
      </div>
      <div class="step-section">
        <h3 class="step-section-title">{{ TEXT.step2Title }}</h3>
        <p class="step-description">{{ TEXT.step2Description }}</p>
      </div>
      <div class="step-section">
        <h3 class="step-section-title">{{ TEXT.step3Title }}</h3>
        <p class="step-description">{{ TEXT.step3Description }}</p>
        <FileUploaderComponent
          ref="fileUploaderRef"
          :accept="ACCEPT_FILE_EXTENSIONS"
          :maxFileSize="MAX_FILE_SIZE"
          :dragDropText="TEXT.dragDropText"
          :fileTypesText="TEXT.fileTypesText"
          :uploadStatusText="TEXT.uploadStatusText"
          :errorStatusText="TEXT.errorStatusText"
          :errorMessage="importErrorMessage"
          @fileSelected="onFileSelected"
          @fileRemoved="onFileRemoved"
          @uploadProgressComplete="onUploadProgressComplete"
        />
      </div>
      <div class="dialog-actions-container">
        <LfActionsBarComponent
          :buttons="buttons"
          @execute-action="onExecuteAction"
          class="action-buttons"
          :showKebabMenu="false"
        />
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import LfBlueTitleComponent from '@/components/HeaderTitle/LfBlueTitleComponent.vue'
import { useI18n } from 'vue-i18n-composable'
import LfActionsBarComponent from '@/components/ActionsBar/LfActionsBarComponent.vue'
import { ActionName } from '@/components/ActionsBar/types/ActionBarComponentTypes'
import { computed, inject, onMounted, ref } from 'vue'
import { LabelButtonVariant } from '@/components/forms/buttons/types/ButtonTypes'
import LfLabelButtonComponent from '@/components/forms/buttons/label/LfLabelButtonComponent.vue'
import { Icons } from '@/icons/icons'
import { createLink } from '@/helpers/blob'
import FileUploaderComponent from '@/views/Expedients/components/ImportExpedientsDialog/components/FileUploaderComponent.vue'
import {
  useImportExpedientsProcessStore
} from '@/views/Expedients/components/ImportExpedientsDialog/stores/useImportExpedientsProcessStore'
import { formatBytes } from '@/helpers/file'

const ACCEPT_FILE_EXTENSIONS = '.xlsx' // Ejemplo multiples extensiones: '.xlsx,.csv'
const MAX_FILE_SIZE = 10 * 1024 * 1024

const closeDialog = inject('closeDialog') as () => void
const nextStep = inject('nextStep') as () => void

// @note: no se desestructura para mantener la reactividad y el estado definido en el componente padre
const importExpedientsProcessStore = useImportExpedientsProcessStore()

const { t } = useI18n()
const TEXT = {
  title: t('components.expedient_import_dialog.title').toString(),
  step1Title: t('components.expedient_import_dialog.uploader.step1_title').toString(),
  step1Description: t('components.expedient_import_dialog.uploader.step1_description').toString(),
  step2Title: t('components.expedient_import_dialog.uploader.step2_title').toString(),
  step2Description: t('components.expedient_import_dialog.uploader.step2_description').toString(),
  step3Title: t('components.expedient_import_dialog.uploader.step3_title').toString(),
  step3Description: t('components.expedient_import_dialog.uploader.step3_description').toString(),
  downloadTemplate: t('components.expedient_import_dialog.uploader.download_template').toString(),
  templateFileName: 'plantilla_importar_expedientes.xlsx',
  close: t('action_buttons.close').toString(),
  import: t('action_buttons.import').toString(),
  // Strings del FileUploaderComponent
  dragDropText: t('components.expedient_import_dialog.uploader.file_uploader.drag_drop_text').toString(),
  fileTypesText: t('components.expedient_import_dialog.uploader.file_uploader.file_types_text').toString(),
  uploadStatusText: t('components.expedient_import_dialog.uploader.file_uploader.upload_status_text').toString(),
  errorStatusText: t('components.expedient_import_dialog.uploader.file_uploader.error_status_text').toString(),
  extensionErrorMessage: t('components.expedient_import_dialog.uploader.file_uploader.extension_error_message').toString(),
  sizeErrorMessage: t('components.expedient_import_dialog.uploader.file_uploader.size_error_message', { maxSize: formatBytes(MAX_FILE_SIZE) }).toString(),
  noContentFileErrorMessage: t('components.expedient_import_dialog.uploader.file_uploader.no_content_file_error_message').toString(),
  defaultErrorMessage: t('components.expedient_import_dialog.uploader.file_uploader.default_error_message').toString(),
  // Alertas
  downloadingSuccess: t('components.expedient_import_dialog.alerts.template_download_success').toString(),
  downloadingError: t('components.expedient_import_dialog.alerts.template_download_error').toString(),
}

// Estados reactivos
const isDownloadingTemplate = ref(false)
const importErrorMessage = ref('')
const uploadProgressEnd = ref(false)
const fileUploadSuccess = ref(false)

const showImportButton = computed((): boolean => uploadProgressEnd.value && fileUploadSuccess.value)

// Función para manejar archivo seleccionado
async function onFileSelected(file: File) {
  fileUploadSuccess.value = false
  if (!isValidFileType(file, ACCEPT_FILE_EXTENSIONS)) {
    importErrorMessage.value = TEXT.extensionErrorMessage
    return
  }

  if (file.size > MAX_FILE_SIZE) {
    importErrorMessage.value = TEXT.sizeErrorMessage
    return
  }

  const uploadResult = await importExpedientsProcessStore.uploadImportExpedientFile(file)

  if (uploadResult.success) {
    fileUploadSuccess.value = true
    importErrorMessage.value = ''
    return
  } else if (uploadResult?.emptyContentError) {
    importErrorMessage.value = TEXT.noContentFileErrorMessage
  } else {
    importErrorMessage.value = TEXT.defaultErrorMessage
  }
}

function onFileRemoved() {
  importErrorMessage.value = ''
  importExpedientsProcessStore.deleteImportExpedienteFile()
  uploadProgressEnd.value = false
  fileUploadSuccess.value = false
}

function onUploadProgressComplete() {
  uploadProgressEnd.value = true
}

const buttons = computed(() => {
  return [
    {
      tooltip: TEXT.close,
      variant: LabelButtonVariant.OUTLINED,
      action: ActionName.CLOSE,
      enabled: true
    },
    {
      tooltip: TEXT.import,
      variant: LabelButtonVariant.SOLID,
      action: ActionName.NEXT,
      enabled: showImportButton.value
    },
  ]
})

async function downloadImportExpedientTemplate() {
  try {
    isDownloadingTemplate.value = true
    const templateFile = await importExpedientsProcessStore.downloadImportExpedientsTemplate()
    createLink(templateFile.downloadUrl, TEXT.templateFileName)
  } catch (error) {
    console.error(TEXT.downloadingError, error)
  } finally {
    isDownloadingTemplate.value = false
  }
}

function isValidFileType(file: File, acceptString: string): boolean {
  const acceptedTypes = acceptString.split(',').map(type => type.trim())
  return acceptedTypes.some(type => {
    if (type.startsWith('.')) {
      return file.name.toLowerCase().endsWith(type.toLowerCase())
    }
    return file.type === type
  })
}

async function onExecuteAction(action: string) {
  switch (action) {
    case ActionName.NEXT:
      nextStep()
      return
    case ActionName.CLOSE:
      closeDialog()
      return
  }
}
</script>

<style lang="scss" scoped>
.step-wizard-container {
  width: 100%;
  max-width: 900px;
  margin: 0 auto;
  padding: $spacing-md;

  .step-section {
    background: transparent;
    border: none;
    border-radius: 0;
    padding: 0;
    margin-bottom: $spacing-md;
    box-shadow: none;

    &:last-child {
      margin-bottom: $spacing-md;
    }
  }

  .step-section-title {
    @include inter-regular-16;
    color: $corporate-color;
    font-size: 16px;
    font-weight: 600;
  }

  .step-description {
    @include inter-regular-14;
    color: $neutral-grey-900;
    margin-bottom: $spacing-md;
    line-height: 1.5;
  }

  .download-button-container {
    margin: $spacing-md 0;
  }
}

.dialog-actions-container {
  @include flex($justify-content: flex-end, $align-items: center);
  gap: $spacing-sm;
  width: 100%;
  margin-top: $spacing-lg;
  padding-top: $spacing-md;
}
</style>
